import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { FeaturesConfig } from "~/backend/generated/Features/model/featuresConfig";
import { FeaturesList } from "~/backend/generated/Features/model/featuresList";
import { Client, ClientEvent, ClientOptions } from "~/modules/client";
import { mergeUserOptions } from "~/utils/mergeUserOptions";
import {
    TelemetrySdkEvent,
    TelemetrySdkEventGroup,
    TelemetrySdkEventName,
    TelemetrySdkEventSource,
    TelemetrySdkClient,
    SDKDependency
} from "~/modules/telemetrySdkClient";

import { getLogger, Logger, LoggerConfigImpl, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import type { DeepPartial } from "~/utils/DeepPartial";
import { ClientOptionsStore } from "../ClientOptions/ClientOptionsStore";
import { throwFlexSdkError, throwFlexSdkErrorFromErrorResponse } from "~/modules/error/ThrowError/ErrorHelper";
import { getTelemetrySdkClient } from "~/modules/telemetrySdkClient/telemetrySdkClient";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { ClientImpl } from "../Client/ClientImpl/ClientImpl";
import { initErrorSubscriptions } from "~/modules/reporter/Subscriber";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { DataClientImpl } from "~/modules/dataClient/DataClientImpl";
import { featuresProviderImpl } from "~/modules/config/FeaturesProvider/featuresProviderImpl/featuresProviderImpl";
import { isFeatureFlagEnabled } from "~/modules/actions/ActionUtils";

const getFeatureNameById = (id: string, featuresConfig: FeaturesConfig) => {
    const features: FeaturesList[] = featuresConfig?.features;
    const feature: FeaturesList | undefined = features?.find((item) => item.id === id);
    return feature?.name;
};

const sendClientInitEvent = async (logger: Logger, telemetrySdkClient: TelemetrySdkClient, durationInMs: number) => {
    try {
        const group = telemetrySdkClient.createEventGroup<TelemetrySdkEvent>(TelemetrySdkEventGroup.Default);
        await group.addEvents({
            eventName: TelemetrySdkEventName.ClientInitialized,
            eventSource: TelemetrySdkEventSource.Client,
            durationMs: durationInMs
        });
    } catch (e) {
        logger.error("Failed to send client init event", e);
    }
};

export const createClient =
    (ctx: ContextManager) =>
    async (token: string, userOptions?: DeepPartial<ClientOptions>): Promise<Client> => {
        initErrorSubscriptions(ctx);
        const t0Ms = Date.now();
        const clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        const loggerConfig = ctx.retrieveInstanceOf(ContextInstance.LoggerConfigImpl, LoggerConfigImpl);
        const logger = getLogger(ctx)(LoggerName.Client);
        const throwError = throwFlexSdkError(ctx);
        const throwErrorFromErrorResponse = throwFlexSdkErrorFromErrorResponse(ctx);

        if (userOptions?.logger?.level) {
            loggerConfig.level = userOptions?.logger?.level;
        }

        mergeUserOptions(clientOptions, userOptions);

        const session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        if (session && !session?.token) {
            try {
                await session.init(token);
            } catch (err) {
                const metadata = {
                    module: "client",
                    severity: ErrorSeverity.Error,
                    source: "createClient.ts"
                };
                
                
                if (err.code === 51102 || err.code === 20003) {
                    metadata.source = "Twilsock";
                    
                    throwError(ErrorCode.Forbidden, metadata, "Insufficient permissions", err.source);
                } else {
                    throwErrorFromErrorResponse(err, metadata);
                }
            }
        }

        try {
            const telemetrySessionData = {
                dependencies: {
                    [SDKDependency.Twilsock]: "default"
                }
            };

            const telemetrySdkClient = getTelemetrySdkClient(ctx);
            await telemetrySdkClient.setSessionData(telemetrySessionData);

            const durationMs = Date.now() - t0Ms;
            
            await sendClientInitEvent(logger, telemetrySdkClient, durationMs);
        } catch (e) {
            logger.error("Failed to set session data for telemetry", e);
        }

        const analytics = ctx.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const config = await featuresProviderImpl(ctx)(token);

        const productAnalyticsDisabled = isFeatureFlagEnabled("disable-flex-sdk-telemetry-beta", config);
        const analyticsKey = getFeatureNameById("flex-telemetry-key", config);
        if (productAnalyticsDisabled) {
            logger.info("Product analytics is disabled");
        } else if (!analyticsKey) {
            logger.info("Analytics Key for Telemetry not found in features config");
        } else {
            try {
                analytics.init(analyticsKey);
            } catch (e) {
                logger.warn("Failed to initialise Analytics", e);
            }
        }

        if (!clientOptions.disableDataClient) {
            const dataClient = ctx.retrieveInstanceOf(ContextInstance.DataClientImpl, DataClientImpl);
            try {
                await dataClient.init();
            } catch (e) {
                logger.error("Failed to initialize DataClient", e);
            }
        }

        const client = new ClientImpl(ctx);
        const taskRouter = ctx.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);

        client.addListener(ClientEvent.TokenUpdated, (tokenArg: string) => taskRouter.updateToken(tokenArg));

        return client;
    };
